// SPDX-License-Identifier: AGPL-3.0-or-later

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {http} from '@app/features/platform/transport/RestTransport';
import type {
	CreateLobbyMessageRequest,
	CreateLobbyRequest,
	LobbyDetailResponse,
	LobbyGameStatsResponse,
	LobbyInviteCreateResponse,
	LobbyInviteGuildsResponse,
	LobbyListResponse,
	LobbyMessageListResponse,
	LobbyMessageResponse,
	UpdateLobbyRequest,
} from '@fluxer/schema/src/domains/lobby/LobbySchemas';

export type LobbyListQuery = {
	limit?: number;
	before?: string;
	game_id?: string;
	language?: string;
	microphone_preference?: string;
	play_style?: string;
	has_slots?: boolean;
	q?: string;
};

export async function fetchLobbies(params?: LobbyListQuery): Promise<LobbyListResponse> {
	const response = await http.get<LobbyListResponse>(Endpoints.LOBBIES, {
		query: {
			limit: params?.limit,
			before: params?.before,
			game_id: params?.game_id,
			language: params?.language,
			microphone_preference: params?.microphone_preference,
			play_style: params?.play_style,
			has_slots: params?.has_slots == null ? undefined : params.has_slots ? 'true' : 'false',
			q: params?.q,
		},
	});
	return response.body;
}

export async function fetchLobbyGameStats(): Promise<LobbyGameStatsResponse> {
	const response = await http.get<LobbyGameStatsResponse>(Endpoints.LOBBY_GAME_STATS);
	return response.body;
}

export async function createLobby(body: CreateLobbyRequest): Promise<LobbyDetailResponse> {
	const response = await http.post<LobbyDetailResponse>(Endpoints.LOBBIES, {body});
	return response.body;
}

export async function fetchLobby(lobbyId: string): Promise<LobbyDetailResponse> {
	const response = await http.get<LobbyDetailResponse>(Endpoints.LOBBY(lobbyId));
	return response.body;
}

export async function updateLobby(lobbyId: string, body: UpdateLobbyRequest): Promise<LobbyDetailResponse> {
	const response = await http.patch<LobbyDetailResponse>(Endpoints.LOBBY(lobbyId), {body});
	return response.body;
}

export async function closeLobby(lobbyId: string): Promise<void> {
	await http.post(Endpoints.LOBBY_CLOSE(lobbyId));
}

export async function adminCloseLobby(lobbyId: string): Promise<void> {
	await http.post(Endpoints.ADMIN_LOBBY_CLOSE(lobbyId));
}

export async function fetchLobbyCreateRestriction(userId: string): Promise<{restricted: boolean}> {
	const response = await http.get<{restricted: boolean}>(Endpoints.ADMIN_LOBBY_CREATE_RESTRICTION(userId));
	return response.body;
}

export async function setLobbyCreateRestriction(userId: string, restricted: boolean): Promise<{restricted: boolean}> {
	const response = await http.put<{restricted: boolean}>(Endpoints.ADMIN_LOBBY_CREATE_RESTRICTION(userId), {
		body: {restricted},
	});
	return response.body;
}

export async function joinLobby(lobbyId: string): Promise<LobbyDetailResponse> {
	const response = await http.post<LobbyDetailResponse>(Endpoints.LOBBY_JOIN(lobbyId));
	return response.body;
}

export async function leaveLobby(lobbyId: string): Promise<void> {
	await http.post(Endpoints.LOBBY_LEAVE(lobbyId));
}

export async function fetchLobbyRequests(lobbyId: string): Promise<LobbyDetailResponse['pending_requests']> {
	const response = await http.get<{requests: NonNullable<LobbyDetailResponse['pending_requests']>}>(
		Endpoints.LOBBY_REQUESTS(lobbyId),
	);
	return response.body.requests;
}

export async function approveLobbyRequest(lobbyId: string, userId: string): Promise<LobbyDetailResponse> {
	const response = await http.post<LobbyDetailResponse>(Endpoints.LOBBY_REQUEST_APPROVE(lobbyId, userId));
	return response.body;
}

export async function rejectLobbyRequest(lobbyId: string, userId: string): Promise<LobbyDetailResponse> {
	const response = await http.post<LobbyDetailResponse>(Endpoints.LOBBY_REQUEST_REJECT(lobbyId, userId));
	return response.body;
}

export async function kickLobbyMember(lobbyId: string, userId: string): Promise<LobbyDetailResponse> {
	const response = await http.delete<LobbyDetailResponse>(Endpoints.LOBBY_MEMBER(lobbyId, userId));
	return response.body;
}

export async function fetchLobbyMessages(
	lobbyId: string,
	params?: {limit?: number; before?: string},
): Promise<LobbyMessageListResponse> {
	const response = await http.get<LobbyMessageListResponse>(Endpoints.LOBBY_MESSAGES(lobbyId), {
		query: {
			limit: params?.limit,
			before: params?.before,
		},
	});
	return response.body;
}

export async function sendLobbyMessage(lobbyId: string, content: string): Promise<LobbyMessageResponse> {
	const body: CreateLobbyMessageRequest = {content};
	const response = await http.post<LobbyMessageResponse>(Endpoints.LOBBY_MESSAGES(lobbyId), {body});
	return response.body;
}

export async function fetchLobbyInviteGuilds(lobbyId: string): Promise<LobbyInviteGuildsResponse> {
	const response = await http.get<LobbyInviteGuildsResponse>(Endpoints.LOBBY_INVITE_GUILDS(lobbyId));
	return response.body;
}

export async function inviteLobbyMember(
	lobbyId: string,
	userId: string,
	body: {guild_id: string; channel_id?: string},
): Promise<LobbyInviteCreateResponse> {
	const response = await http.post<LobbyInviteCreateResponse>(Endpoints.LOBBY_MEMBER_INVITE(lobbyId, userId), {
		body,
	});
	return response.body;
}
